import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import type { TemplateCategoryEntity } from "@/modules/template/entities/templateCategoryEntity"
import { TemplateWeekFormValidationSchema } from "@/modules/template/forms/templateFormValidation"
import { useCreateWeekTemplate } from "@/modules/template/hooks/useTemplate"
import { useTemplateCategoriesInfinite } from "@/modules/template/hooks/useTemplateCategory"
import { CheckIcon, ChevronsUpDownIcon, LoaderCircleIcon } from "lucide-react"
import { useState } from "react"

type TemplateSaveWeekFormProps = {
  sessionIds: string[]
  className?: string
} & React.ComponentProps<"div">

type TemplateWeekFormValues = {
  name: string
  description: string | null
  category: TemplateCategoryEntity | null
  sessionIds: string[]
}

export function TemplateSaveWeekForm({ sessionIds, className, ...props }: TemplateSaveWeekFormProps) {
  const { hideSidebar } = useSidebar()
  const { mutateAsync: createWeekTemplate } = useCreateWeekTemplate()
  const [open, setOpen] = useState(false)
  const { data } = useTemplateCategoriesInfinite({
    searchTerm: undefined,
    page: 0,
    pageSize: 150,
  })

  const defaultValues: TemplateWeekFormValues = {
    name: "",
    description: "",
    category: null,
    sessionIds: sessionIds,
  }

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
    validators: {
      onSubmit: TemplateWeekFormValidationSchema,
      onBlur: TemplateWeekFormValidationSchema,
    },
    onSubmit: ({ value }) => {
      void createWeekTemplate({
        name: value.name,
        description: value.description,
        templateCategoryId: value.category?.id ?? null,
        sessionIds: value.sessionIds,
      })
        .then(() => {
          toast.success("Week template created successfully", {
            classNames: successToastClassName(),
          })
          hideSidebar(false)
        })
        .catch(() => {
          toast.error("Failed to create week template", {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  return (
    <div className={cn("flex flex-1 flex-col gap-4 px-6 pt-18 pb-6", className)} {...props}>
      <Text fontStyle="TL">{word("template.form.saveAsWeekTemplate.title")}</Text>

      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col gap-5">
          <form.AppField name="name">
            {(field) => (
              <field.Field>
                <field.Label className="uppercase">{word("global.form.label.defaultName")}</field.Label>
                <field.Content>
                  <field.Input name="name" placeholder={word("template.form.name.placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.Field>
                <field.Label className="uppercase">{word("global.form.label.shortDescription")}</field.Label>
                <field.Content>
                  <field.Textarea
                    name="description"
                    placeholder={word("template.form.description.placeholder")}
                    rows={4}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="category">
            {(field) => (
              <field.Field>
                <field.Label className="uppercase">{word("global.form.label.shortDescription")}</field.Label>
                <field.Content>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger
                      render={
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={open}
                          className="h-11 w-full justify-between"
                        >
                          {field.state.value?.name ?? word("global.form.label.category")}
                          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      }
                    />
                    <PopoverContent className="w-auto border-0 p-0" align="center">
                      <Command
                        className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
                        filter={(value, search) => {
                          if (value.toLowerCase().includes(search.toLowerCase())) return 1
                          return 0
                        }}
                      >
                        <CommandInput placeholder={word("global.search.placeholder")} className="" />
                        <CommandList>
                          <CommandEmpty>{word("global.search.notFound")}</CommandEmpty>
                          <CommandGroup>
                            {data.map((category) => (
                              <CommandItem
                                key={category.id}
                                value={category.name}
                                onSelect={() => {
                                  field.handleChange(category)
                                  setOpen(false)
                                }}
                                className="cursor-pointer hover:bg-neutral-200"
                              >
                                <CheckIcon
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    field.state.value?.id === category.id ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                {category.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <form.Subscribe selector={(state) => [state.isSubmitting, state.isPristine]}>
          {([isSubmitting, isPristine]) => (
            <div className="flex flex-row justify-end gap-2">
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                disabled={isSubmitting}
                onClick={() => {
                  hideSidebar()
                }}
              >
                {word("global.cancel")}
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="flex-1"
                onClick={() => void form.handleSubmit()}
                disabled={isSubmitting || isPristine}
              >
                {form.state.isSubmitting && <LoaderCircleIcon className="animate-spin" />}
                {word("global.save")}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </div>
    </div>
  )
}
